import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Badge, ProgressBar, ListGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import Layout from "../layout/default";

function Home() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get authentication token (adjust based on your auth system)
  const getAuthToken = () => {
    return localStorage.getItem('authToken') ||
           localStorage.getItem('token') ||
           sessionStorage.getItem('authToken') ||
           null;
  };

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = getAuthToken();
        const headers = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch('http://192.168.1.16:8000/dashboard/analytics/', {
          method: 'GET',
          headers,
          credentials: 'include',
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication required. Please login again.');
          }
          throw new Error(`Failed to fetch dashboard data: ${response.status}`);
        }

        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        setError(err.message || 'Unknown error');
        console.error('Dashboard API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Get summary data with safe fallbacks
  const summary = dashboardData?.summary || {};
  const totalStudents = summary.total_students || 0;
  const totalUniversities = summary.total_universities || 0;
  const totalOrganizations = summary.total_organizations || 0;
  const totalColleges = summary.total_colleges || 0;
  const totalCourses = summary.total_courses || 0;
  const totalEnrolled = summary.total_enrolled_students || 0;
  const avgEnrollmentsPerStudent = summary.avg_enrollments_per_student || 0;
  const mostEnrolledCourse = summary.most_enrolled_course || {};
  const topCourses = summary.top_courses_list || [];
  const totalChallenges = summary.total_challenges || 0;
  const avgSuccessRate = summary.avg_challenge_success_rate || 0;
  const maxScoreChallenge = summary.max_score_challenge || 0;
  
  // New certification metrics
  const totalCertifications = summary.total_certifications || 0;
  const totalCertAttempts = summary.total_cert_attempts || 0;
  const certPassRate = summary.cert_pass_rate || 0;
  const activeUsers7Days = summary.active_users_7_days || 0;

  // Weekly trends data - updated field names
  const weeklyStudentSignup = Array.isArray(dashboardData?.weekly_student_signup) ? dashboardData.weekly_student_signup : [];
  const weeklyChallengeTrends = Array.isArray(dashboardData?.weekly_challenge_trends) ? dashboardData.weekly_challenge_trends : [];
  const weeklyCertificationTrends = Array.isArray(dashboardData?.weekly_certification_trends) ? dashboardData.weekly_certification_trends : [];

  const weeklyStudentData = weeklyStudentSignup.length ? weeklyStudentSignup.map(w => Number(w.new_students || 0)) : [0];
  const weeklyChallengeData = weeklyChallengeTrends.length ? weeklyChallengeTrends.map(w => Number(w.submissions || 0)) : [0];
  const weeklyCertificationData = weeklyCertificationTrends.length ? weeklyCertificationTrends.map(w => Number(w.completed || 0)) : [0];

  // Compute week-over-week percentage changes
  const studentWeekChangeText = (() => {
    if (weeklyStudentData.length >= 2) {
      const latest = weeklyStudentData[0] || 0;
      const prev = weeklyStudentData[1] || 0;
      if (prev === 0) return latest > 0 ? '+100%' : '0%';
      const percent = ((latest - prev) / prev) * 100;
      const sign = percent >= 0 ? '+' : '';
      return `${sign}${percent.toFixed(1)}%`;
    }
    return '0%';
  })();

  const challengeWeekChangeText = (() => {
    if (weeklyChallengeData.length >= 2) {
      const latest = weeklyChallengeData[0] || 0;
      const prev = weeklyChallengeData[1] || 0;
      if (prev === 0) return latest > 0 ? '+100%' : '0%';
      const percent = ((latest - prev) / prev) * 100;
      const sign = percent >= 0 ? '+' : '';
      return `${sign}${percent.toFixed(1)}%`;
    }
    return '0%';
  })();

  const certWeekChangeText = (() => {
    if (weeklyCertificationData.length >= 2) {
      const latest = weeklyCertificationData[0] || 0;
      const prev = weeklyCertificationData[1] || 0;
      if (prev === 0) return latest > 0 ? '+100%' : '0%';
      const percent = ((latest - prev) / prev) * 100;
      const sign = percent >= 0 ? '+' : '';
      return `${sign}${percent.toFixed(1)}%`;
    }
    return '0%';
  })();

  // Top courses from API or fallback
  const topCoursesList = topCourses.length > 0 
    ? topCourses.slice(0, 5).map((course, index) => ({
        id: course.course_id || index,
        name: course.title || `Course ${index + 1}`,
        enrollments: course.enrollments || 0,
        progress: Math.min(100, Math.round((course.enrollments / Math.max(totalEnrolled, 1)) * 100))
      }))
    : [
        { id: 1, name: "Computer Science", enrollments: 1, progress: 100 },
        { id: 2, name: "Data Structures", enrollments: 0, progress: 0 },
        { id: 3, name: "Algorithms", enrollments: 0, progress: 0 },
        { id: 4, name: "Web Development", enrollments: 0, progress: 0 },
        { id: 5, name: "Database Systems", enrollments: 0, progress: 0 }
      ];

  if (loading) {
    return (
      <Layout title="Platform Admin Dashboard">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Platform Admin Dashboard">
        <div className="text-center py-5">
          <div className="text-danger">
            <i className="bi bi-x-circle fs-1 mb-3"></i>
            <p className="mt-2">Error loading dashboard: {error}</p>
            <button 
              className="btn btn-primary mt-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  return (
    <Layout title="Platform Admin Dashboard">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-2">Welcome, {userData.name || 'Platform Admin'}!</h2>
            <p className="text-muted">Here's an overview of the platform administration</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="g-3">
        <Col md={3}>
          <Card className="mb-3 border-0 shadow-sm" style={{
            borderLeft: "4px solid #007bff",
            transition: "transform 0.2s"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{
                  backgroundColor: "#cfe2ff",
                  width: "50px",
                  height: "50px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <i className="bi bi-people-fill fs-4" style={{ color: "#007bff" }}></i>
                </div>
                <h2 className="mb-0" style={{ color: "#007bff" }}>{totalStudents}</h2>
              </div>
              <h6 className="text-muted mb-2">Total Students</h6>
              <span className="text-primary text-decoration-none small">{totalEnrolled} enrolled ({avgEnrollmentsPerStudent}/student)</span>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="mb-3 border-0 shadow-sm" style={{
            borderLeft: "4px solid #28a745",
            transition: "transform 0.2s"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{
                  backgroundColor: "#d4edda",
                  width: "50px",
                  height: "50px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <i className="bi bi-building fs-4" style={{ color: "#28a745" }}></i>
                </div>
                <h2 className="mb-0" style={{ color: "#28a745" }}>{totalColleges + totalUniversities}</h2>
              </div>
              <h6 className="text-muted mb-2">Institutions</h6>
              <span className="text-success text-decoration-none small">
                {totalColleges} colleges, {totalUniversities} universities
              </span>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="mb-3 border-0 shadow-sm" style={{
            borderLeft: "4px solid #ffc107",
            transition: "transform 0.2s"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{
                  backgroundColor: "#fff3cd",
                  width: "50px",
                  height: "50px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <i className="bi bi-book-fill fs-4" style={{ color: "#ffc107" }}></i>
                </div>
                <h2 className="mb-0" style={{ color: "#ffc107" }}>{totalCourses}</h2>
              </div>
              <h6 className="text-muted mb-2">Total Courses</h6>
              <span className="text-warning text-decoration-none small">{topCourses.length} active courses</span>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="mb-3 border-0 shadow-sm" style={{
            borderLeft: "4px solid #dc3545",
            transition: "transform 0.2s"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div style={{
                  backgroundColor: "#f8d7da",
                  width: "50px",
                  height: "50px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <i className="bi bi-award fs-4" style={{ color: "#dc3545" }}></i>
                </div>
                <h2 className="mb-0" style={{ color: "#dc3545" }}>{totalCertifications}</h2>
              </div>
              <h6 className="text-muted mb-2">Certifications</h6>
              <span className="text-danger text-decoration-none small">{certPassRate}% pass rate</span>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4 g-3">
        <Col md={8}>
          {/* Platform Growth Summary */}
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header style={{
              background: "linear-gradient(to right, #667eea, #764ba2)",
              color: "#fff",
              fontWeight: "bold"
            }}>
              Platform Growth Overview
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-center p-3 border rounded bg-light">
                    <i className="bi bi-person-plus fs-2 text-primary mb-2"></i>
                    <h5 className="text-primary">{weeklyStudentData[0] || 0}</h5>
                    <p className="text-muted mb-1">New Students</p>
                    <Badge bg={studentWeekChangeText.includes('+') ? "success" : studentWeekChangeText.includes('-') ? "danger" : "secondary"}>
                      {studentWeekChangeText}
                    </Badge>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center p-3 border rounded bg-light">
                    <i className="bi bi-trophy fs-2 text-warning mb-2"></i>
                    <h5 className="text-warning">{weeklyChallengeData[0] || 0}</h5>
                    <p className="text-muted mb-1">Challenge Submissions</p>
                    <Badge bg={challengeWeekChangeText.includes('+') ? "success" : challengeWeekChangeText.includes('-') ? "danger" : "secondary"}>
                      {challengeWeekChangeText}
                    </Badge>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center p-3 border rounded bg-light">
                    <i className="bi bi-award fs-2 text-success mb-2"></i>
                    <h5 className="text-success">{weeklyCertificationData[0] || 0}</h5>
                    <p className="text-muted mb-1">Certifications Completed</p>
                    <Badge bg={certWeekChangeText.includes('+') ? "success" : certWeekChangeText.includes('-') ? "danger" : "secondary"}>
                      {certWeekChangeText}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Most Popular Courses */}
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header style={{
              background: "linear-gradient(to right, #667eea, #764ba2)",
              color: "#fff",
              fontWeight: "bold"
            }}>
              Most Popular Courses
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {topCoursesList.map(course => (
                  <ListGroup.Item key={course.id} className="px-0 py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-book me-2 text-primary"></i>
                        <span className="fw-bold">{course.name}</span>
                      </div>
                      <Badge bg="primary">{course.enrollments} students</Badge>
                    </div>
                    <ProgressBar 
                      now={course.progress} 
                      className="progress-sm" 
                      variant={course.enrollments > 0 ? "primary" : "secondary"}
                    />
                    <small className="text-muted">{course.progress}% of total enrollments</small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>

            <Card className="shadow-sm border-0 mt-4">
            <Card.Header style={{
              background: "linear-gradient(to right, #667eea, #764ba2)",
              color: "#fff",
              fontWeight: "bold"
            }}>
              Platform Summary
            </Card.Header>
            <Card.Body>
              <div className="mb-2">
                <strong>Total Organizations:</strong> <span className="text-muted">{totalOrganizations}</span>
              </div>
              <div className="mb-2">
                <strong>Most Popular Course:</strong> <span className="text-muted">{mostEnrolledCourse.course__title || mostEnrolledCourse.title || 'Computer Science'}</span>
              </div>
              <div className="mb-2">
                <strong>Course Enrollments:</strong> <span className="text-muted">{mostEnrolledCourse.enroll_count || 1}</span>
              </div>
              <div className="mb-2">
                <strong>Total Enrolled Students:</strong> <span className="text-muted">{totalEnrolled}</span>
              </div>
              <div>
                <strong>Active Users (7 days):</strong> <span className="text-muted">{activeUsers7Days}</span>
              </div>
            </Card.Body>
          </Card>

          </Card>

          {/* Additional Statistics Row */}
      <Row className="mt-3 g-3">

        <Col md={6}>
          <Card className="shadow-sm border-0">
            <Card.Header style={{
              background: "linear-gradient(to right, #667eea, #764ba2)",
              color: "#fff",
              fontWeight: "bold"
            }}>
              Recent Activity
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-people-fill me-2 text-success"></i>
                  <span className="small">{totalEnrolled} students currently enrolled in courses</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-building me-2 text-info"></i>
                  <span className="small">{totalOrganizations} organizations across {totalColleges} colleges</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-book me-2 text-primary"></i>
                  <span className="small">{totalCourses} courses available with {topCourses.length} active</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-award me-2 text-warning"></i>
                  <span className="small">{totalCertifications} certifications with {certPassRate}% success rate</span>
                </div>
                <div className="d-flex align-items-center">
                  <i className="bi bi-trophy me-2 text-danger"></i>
                  <span className="small">{totalChallenges} coding challenges available</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
        </Col>

        <Col md={4}>
          {/* Quick Actions */}
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header style={{
              background: "linear-gradient(to right, #667eea, #764ba2)",
              color: "#fff",
              fontWeight: "bold"
            }}>
              Quick Actions
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Link to="/Certificates/list-certificate" className="btn btn-outline-primary">
                  <i className="bi bi-award me-2"></i> Manage Certificates
                </Link>
                <Link to="/Colleges/list-college" className="btn btn-outline-success">
                  <i className="bi bi-building me-2"></i> Manage Institutions
                </Link>
                <Link to="/Courses/list-course" className="btn btn-outline-info">
                  <i className="bi bi-book me-2"></i> View All Courses
                </Link>
                <Link to="/CodingChallenges/list-challenge" className="btn btn-outline-warning">
                  <i className="bi bi-trophy me-2"></i> Coding Challenges
                </Link>
              </div>
            </Card.Body>
          </Card>

          {/* Challenge & Certification Statistics */}
          <Card className="shadow-sm border-0">
            <Card.Header style={{
              background: "linear-gradient(to right, #667eea, #764ba2)",
              color: "#fff",
              fontWeight: "bold"
            }}>
              Performance Metrics
            </Card.Header>
            <Card.Body>
              {/* Challenge Stats */}
              <div className="text-center mb-4 p-3 border-bottom">
                <i className="bi bi-trophy fs-1 text-warning mb-2"></i>
                <h3 className="text-warning">{totalChallenges}</h3>
                <p className="text-muted mb-0">Total Challenges</p>
                {avgSuccessRate > 0 && (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small">Success Rate</span>
                      <span className="small text-muted">{Math.round(avgSuccessRate * 100)}%</span>
                    </div>
                    <ProgressBar 
                      now={Math.round(avgSuccessRate * 100)} 
                      className="progress-sm" 
                      variant="success"
                    />
                  </div>
                )}
              </div>
              
              {/* Certification Stats */}
              <div className="text-center p-3">
                <i className="bi bi-award fs-1 text-success mb-2"></i>
                <h3 className="text-success">{totalCertifications}</h3>
                <p className="text-muted mb-0">Total Certifications</p>
                <div className="mt-2">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="small">Pass Rate</span>
                    <span className="small text-muted">{certPassRate}%</span>
                  </div>
                  <ProgressBar 
                    now={certPassRate} 
                    className="progress-sm" 
                    variant="info"
                  />
                </div>
                <div className="mt-2 d-flex justify-content-between">
                  <small className="text-muted">Attempts: {totalCertAttempts}</small>
                  <small className="text-muted">Active Users: {activeUsers7Days}</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

    </Layout>
  );
}

export default Home;